import {
  Alert,
  Box,
  Button,
  Divider,
  FormGroup,
  FormLabel,
  TextField,
  Typography,
} from "@mui/material";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import {
  DiaryEntry as DiaryEntryType,
  createDiaryEntry,
  deleteDiaryEntry,
  getCompanyDiary,
  updateDiaryEntry,
} from "src/apis/diary";
import CompanySelect from "src/pages/components/CompanySelect";
import PageContainer from "src/pages/components/PageContainer";
import { isUserAdministrator } from "src/utils/auth";
import { getToday } from "src/utils/date";
import { Id } from "src/utils/types";
import DiaryEntriesTable from "./components/DiaryEntriesTable";

type FormState = {
  titulo: string;
  mensagem: string;
  data: string;
  numero_participantes: string;
};

const emptyForm = (): FormState => ({
  titulo: "",
  mensagem: "",
  data: getToday(),
  numero_participantes: "",
});

const hasCompanyFilter = (id: Id): boolean => Boolean(id) && String(id) !== "-1";

const DiaryEntry: FC = (): JSX.Element => {
  const [companyId, setCompanyId] = useState<Id>("");
  const [filterCompanyId, setFilterCompanyId] = useState<Id>("");
  const [form, setForm] = useState<FormState>(emptyForm());
  const [editId, setEditId] = useState<number | null>(null);
  const [entries, setEntries] = useState<DiaryEntryType[]>([]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const patch = (next: Partial<FormState>) =>
    setForm((prev) => ({ ...prev, ...next }));

  const loadEntries = useCallback(async () => {
    try {
      const data = await getCompanyDiary(
        hasCompanyFilter(filterCompanyId)
          ? { company_id: filterCompanyId }
          : {}
      );
      setEntries(data);
      setError("");
    } catch (err: any) {
      setError(err?.message ?? "Erro ao carregar entradas.");
    }
  }, [filterCompanyId]);

  useEffect(() => {
    loadEntries();
  }, [loadEntries]);

  const resetForm = () => {
    setForm(emptyForm());
    setEditId(null);
  };

  const isSubmitDisabled = useMemo(
    () =>
      isSaving ||
      !companyId ||
      !form.titulo.trim() ||
      !form.mensagem.trim() ||
      !form.data,
    [isSaving, companyId, form]
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setError("");
    setSuccess("");
    try {
      const payload = {
        company_id: String(companyId),
        titulo: form.titulo.trim(),
        mensagem: form.mensagem.trim(),
        data: form.data,
        numero_participantes: form.numero_participantes
          ? Number(form.numero_participantes)
          : null,
      };

      if (editId !== null) {
        await updateDiaryEntry(editId, payload);
        setSuccess("Entrada atualizada com sucesso!");
      } else {
        await createDiaryEntry(payload);
        setSuccess("Entrada adicionada ao diário com sucesso!");
      }

      resetForm();
      await loadEntries();
    } catch (err: any) {
      setError(err?.message ?? "Não foi possível salvar a entrada.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleEdit = (entry: DiaryEntryType) => {
    setEditId(entry.id);
    setCompanyId(String(entry.fk_company_id));
    setForm({
      titulo: entry.titulo,
      mensagem: entry.mensagem,
      data: entry.data,
      numero_participantes:
        entry.numero_participantes != null
          ? String(entry.numero_participantes)
          : "",
    });
    setSuccess("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("Remover esta entrada do diário?")) return;
    try {
      await deleteDiaryEntry(id);
      if (editId === id) resetForm();
      await loadEntries();
    } catch (err: any) {
      setError(err?.message ?? "Erro ao remover.");
    }
  };

  return (
    <PageContainer title="Entrada Diário" error={error} guard={isUserAdministrator}>
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{ display: "flex", flexDirection: "column", gap: 2, maxWidth: 640 }}
      >
        {Boolean(success) && <Alert severity="success">{success}</Alert>}

        <FormGroup>
          <FormLabel required>Empresa</FormLabel>
          <CompanySelect
            companyId={companyId}
            setCompanyId={(id: Id) => {
              setCompanyId(id);
              setSuccess("");
            }}
            setError={setError}
          />
        </FormGroup>

        <FormGroup>
          <FormLabel required>Título</FormLabel>
          <TextField
            value={form.titulo}
            onChange={(e) => patch({ titulo: e.target.value })}
            placeholder="Ex: Treinamento: Liderança e Saúde Mental"
          />
        </FormGroup>

        <FormGroup>
          <FormLabel required>Mensagem</FormLabel>
          <TextField
            value={form.mensagem}
            onChange={(e) => patch({ mensagem: e.target.value })}
            multiline
            minRows={3}
            placeholder="Descreva a ação realizada na empresa."
          />
        </FormGroup>

        <FormGroup>
          <FormLabel required>Data</FormLabel>
          <TextField
            type="date"
            InputLabelProps={{ shrink: true }}
            value={form.data}
            onChange={(e) => patch({ data: e.target.value })}
          />
        </FormGroup>

        <FormGroup>
          <FormLabel>Número de participantes</FormLabel>
          <TextField
            type="number"
            inputProps={{ min: 0 }}
            value={form.numero_participantes}
            onChange={(e) => patch({ numero_participantes: e.target.value })}
          />
        </FormGroup>

        <Box sx={{ display: "flex", gap: 1 }}>
          <Button type="submit" variant="contained" disabled={isSubmitDisabled}>
            {editId !== null ? "Salvar alterações" : "Adicionar entrada"}
          </Button>
          {editId !== null && (
            <Button type="button" onClick={resetForm} disabled={isSaving}>
              Cancelar edição
            </Button>
          )}
        </Box>
      </Box>

      <Divider sx={{ my: 3 }} />

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1,
          mb: 2,
        }}
      >
        <Typography variant="h6">Eventos registrados</Typography>
        <CompanySelect
          isFilter
          companyId={filterCompanyId}
          setCompanyId={setFilterCompanyId}
          setError={setError}
          useAllOption
        />
      </Box>

      <DiaryEntriesTable
        entries={entries}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />
    </PageContainer>
  );
};

export default DiaryEntry;
